import React, { useState, useRef, useEffect, useMemo } from 'react';
// No DatePicker here on purpose (2026-09-18, moves-architecture.md rule 1):
// this editor writes addresses / stops / access details only. Dates live
// on the move and are edited through the TripStrip / MoveHeaderCard.
import { AddressInput, FormField, NumberField, ChoiceKeys, Combobox, Button, IconButton } from '../ui';
import { IconPlus, IconTruck } from '../../components/icons';
import { PACKING_OPTIONS, PACKING_LABELS, DRIVEWAY_OPTIONS, WALK_DISTANCE_OPTIONS, JOB_TYPES } from '../../lib/constants';

export function SectionHeader({ title, subtitle }) {
    return (
        <div className="mb-4 mt-6 first:mt-0 pb-2 border-b border-slate-100">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</h3>
            {subtitle && <p className="text-[11px] text-slate-400 mt-0.5">{subtitle}</p>}
            <div className="h-px bg-slate-200 mt-2"></div>
        </div>
    );
}

// Apartment floor picker options — 20 floors is past the ChoiceKeys
// limit, so it's a Combobox. Values stay strings (what the old native
// dropdown handed to e.target.value).
const ordinal = (f) => `${f}${f === 1 ? 'st' : f === 2 ? 'nd' : f === 3 ? 'rd' : 'th'}`;
const FLOOR_ITEMS = Array.from({ length: 20 }, (_, i) => i + 1).map(f => ({ value: String(f), label: `${ordinal(f)} Floor` }));

export function LocationFields({ prefix, label, form, set, color, readOnly = false }) {
    const borderColor = color === 'red' ? 'border-red-200' : color === 'blue' ? 'border-blue-200' : 'border-slate-200';
    const bgColor = color === 'red' ? 'bg-red-50/40' : color === 'blue' ? 'bg-blue-50/40' : 'bg-slate-50';
    const labelColor = color === 'red' ? 'text-red-700' : color === 'blue' ? 'text-blue-700' : 'text-slate-800';
    return (
        <div className={`${bgColor} rounded-lg p-4 border ${borderColor}`}>
            <h4 className={`text-sm font-semibold ${labelColor} mb-3`}>{label}</h4>
            <div className="grid grid-cols-2 gap-3">
                <FormField label="Sq Footage"><NumberField step={100} value={form[`${prefix}_sqft`] || ''} onChange={v => set(`${prefix}_sqft`, parseInt(v) || 0)} placeholder="1500" /></FormField>
                <FormField label="Levels"><NumberField step={1} min={1} value={form[`${prefix}_levels`] || 1} onChange={v => set(`${prefix}_levels`, parseInt(v) || 1)} /></FormField>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <FormField label="Driveway"><ChoiceKeys size="sm" allowEmpty emptyLabel="—" items={DRIVEWAY_OPTIONS} value={form[`${prefix}_driveway`] || ''} onChange={v => set(`${prefix}_driveway`, v)} /></FormField>
                <FormField label="Walk to Truck"><ChoiceKeys size="sm" allowEmpty emptyLabel="—" items={WALK_DISTANCE_OPTIONS} value={form[`${prefix}_walk_distance`] || ''} onChange={v => set(`${prefix}_walk_distance`, v)} /></FormField>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
                {/* Stairs / Elevator / Apartment as button-style toggles
                    rather than tiny checkboxes — the brothers reported
                    "the buttons don't function" because the affordance
                    didn't read as clickable. Pressed state uses the
                    panel's accent color. When Stairs is on, a "Steps"
                    quick-pick + free-form input appears inline so the
                    dispatcher captures how many stairs the crew faces
                    at the entrance (separate from _levels, which is
                    stories of the building). */}
                <ToggleButton
                    pressed={!!form[`${prefix}_stairs`]}
                    onClick={() => set(`${prefix}_stairs`, !form[`${prefix}_stairs`])}
                    color={color}
                    disabled={readOnly}
                >
                    Stairs
                </ToggleButton>
                <ToggleButton
                    pressed={!!form[`${prefix}_elevator`]}
                    onClick={() => set(`${prefix}_elevator`, !form[`${prefix}_elevator`])}
                    color={color}
                    disabled={readOnly}
                >
                    Elevator
                </ToggleButton>
                <ToggleButton
                    pressed={!!form[`${prefix}_apartment`]}
                    onClick={() => set(`${prefix}_apartment`, !form[`${prefix}_apartment`])}
                    color={color}
                    disabled={readOnly}
                >
                    Apartment
                </ToggleButton>
                {form[`${prefix}_apartment`] && (
                    <Combobox size="sm" className="w-40" disabled={readOnly} items={FLOOR_ITEMS} value={form[`${prefix}_apartment_floor`] || ''} onChange={v => set(`${prefix}_apartment_floor`, v)} placeholder="Floor..." ariaLabel="Apartment floor" />
                )}
            </div>
            {form[`${prefix}_stairs`] && (
                <StairsSteps
                    value={form[`${prefix}_stairs_steps`]}
                    onChange={v => set(`${prefix}_stairs_steps`, v)}
                    disabled={readOnly}
                />
            )}
        </div>
    );
}

// ── Toggle button — visual replacement for the bare checkboxes that
//    Jaylen's brothers reported as un-clickable / invisible. Reads as a
//    rounded pill: outline when off, filled with the panel's accent when
//    on. Same color tokens as LocationFields' label (red / blue / slate)
//    so it ties visually to its parent block.
function ToggleButton({ pressed, onClick, color, disabled, children }) {
    const onClassByColor = {
        red:   'bg-red-600 text-white border-red-600 shadow-sm',
        blue:  'bg-blue-600 text-white border-blue-600 shadow-sm',
        slate: 'bg-slate-700 text-white border-slate-700 shadow-sm',
    };
    const offClass = 'bg-white text-slate-700 border-slate-300 hover:border-slate-400 hover:bg-slate-50';
    const onClass = onClassByColor[color] || onClassByColor.slate;
    return (
        <button
            type="button"
            role="switch"
            aria-checked={pressed}
            onClick={onClick}
            disabled={disabled}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${pressed ? onClass : offClass}`}
        >
            {pressed && <span aria-hidden="true">✓</span>}
            {children}
        </button>
    );
}

// ── Steps quick-pick — appears below the Stairs toggle when it's on.
//    Common values get one-tap buttons (3 / 5 / 8 / 12); for anything
//    else there's a free-form integer input. Stored on the client as
//    {prefix}_stairs_steps (sql/041_stairs_steps.sql).
function StairsSteps({ value, onChange, disabled }) {
    const QUICK = [3, 5, 8, 12];
    const num = value == null ? null : Number(value);
    return (
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-500 font-semibold">Steps to entrance:</span>
            {QUICK.map(n => (
                <button
                    key={n}
                    type="button"
                    disabled={disabled}
                    onClick={() => onChange(n)}
                    className={`px-2.5 py-1 rounded-md text-xs font-bold border transition-colors disabled:opacity-50 ${
                        num === n
                            ? 'bg-amber-500 text-white border-amber-500'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400 hover:bg-amber-50'
                    }`}
                >
                    {n}
                </button>
            ))}
            <NumberField
                size="sm"
                step={1}
                min={1}
                disabled={disabled}
                value={value ?? ''}
                onChange={v => onChange(v === '' ? null : Math.max(0, parseInt(v, 10) || 0))}
                placeholder="other"
                ariaLabel="Steps to entrance"
                className="w-32"
            />
            {num != null && num > 0 && (
                <Button
                    variant="tertiary"
                    size="sm"
                    disabled={disabled}
                    onClick={() => onChange(null)}
                >
                    clear
                </Button>
            )}
        </div>
    );
}

function StopLocationFields({ stopIndex, num, form, set, readOnly = false }) {
    const prefix = `stop_${stopIndex}`;
    const stopColors = ['emerald', 'amber', 'purple', 'cyan', 'orange', 'pink'];
    const colorName = stopColors[stopIndex % stopColors.length];
    const colorMap = { emerald: { bg: 'bg-emerald-50/40', border: 'border-emerald-200', label: 'text-emerald-700' }, amber: { bg: 'bg-amber-50/40', border: 'border-amber-200', label: 'text-amber-700' }, purple: { bg: 'bg-purple-50/40', border: 'border-purple-200', label: 'text-purple-700' }, cyan: { bg: 'bg-cyan-50/40', border: 'border-cyan-200', label: 'text-cyan-700' }, orange: { bg: 'bg-orange-50/40', border: 'border-orange-200', label: 'text-orange-700' }, pink: { bg: 'bg-pink-50/40', border: 'border-pink-200', label: 'text-pink-700' } };
    const c = colorMap[colorName];
    return (
        <div className={`${c.bg} rounded-lg p-4 border ${c.border}`}>
            <h4 className={`text-sm font-semibold ${c.label} mb-3`}>Stop {num || stopIndex + 1}</h4>
            <div className="grid grid-cols-2 gap-3">
                <FormField label="Sq Footage"><NumberField step={100} value={form[`${prefix}_sqft`] || ''} onChange={v => set(`${prefix}_sqft`, parseInt(v) || 0)} placeholder="1500" /></FormField>
                <FormField label="Levels"><NumberField step={1} min={1} value={form[`${prefix}_levels`] || 1} onChange={v => set(`${prefix}_levels`, parseInt(v) || 1)} /></FormField>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <FormField label="Driveway"><ChoiceKeys size="sm" allowEmpty emptyLabel="—" items={DRIVEWAY_OPTIONS} value={form[`${prefix}_driveway`] || ''} onChange={v => set(`${prefix}_driveway`, v)} /></FormField>
                <FormField label="Walk to Truck"><ChoiceKeys size="sm" allowEmpty emptyLabel="—" items={WALK_DISTANCE_OPTIONS} value={form[`${prefix}_walk_distance`] || ''} onChange={v => set(`${prefix}_walk_distance`, v)} /></FormField>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
                <ToggleButton
                    pressed={!!form[`${prefix}_stairs`]}
                    onClick={() => set(`${prefix}_stairs`, !form[`${prefix}_stairs`])}
                    color="slate"
                    disabled={readOnly}
                >Stairs</ToggleButton>
                <ToggleButton
                    pressed={!!form[`${prefix}_elevator`]}
                    onClick={() => set(`${prefix}_elevator`, !form[`${prefix}_elevator`])}
                    color="slate"
                    disabled={readOnly}
                >Elevator</ToggleButton>
            </div>
            {form[`${prefix}_stairs`] && (
                <StairsSteps
                    value={form[`${prefix}_stairs_steps`]}
                    onChange={v => set(`${prefix}_stairs_steps`, v)}
                    disabled={readOnly}
                />
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
                <ToggleButton
                    pressed={!!form[`${prefix}_apartment`]}
                    onClick={() => set(`${prefix}_apartment`, !form[`${prefix}_apartment`])}
                    color="slate"
                    disabled={readOnly}
                >Apartment</ToggleButton>
                {form[`${prefix}_apartment`] && (
                    <Combobox size="sm" className="w-40" disabled={readOnly} items={FLOOR_ITEMS} value={form[`${prefix}_apartment_floor`] || ''} onChange={v => set(`${prefix}_apartment_floor`, v)} placeholder="Floor..." ariaLabel="Apartment floor" />
                )}
            </div>
        </div>
    );
}

export default function MoveDetailsEditor({ form, set, setBatch, readOnly = false, showDistances = true, compact = false }) {
    const inputCls = 'w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-400 placeholder:text-slate-400';
    const parseStops = (val) => {
        if (!val) return [];
        try { const parsed = JSON.parse(val); if (Array.isArray(parsed)) return parsed; } catch(e) {}
        return val.trim() ? [{ address: val.trim() }] : [];
    };
    const stopIdRef = useRef(0);
    const [routePoints, setRoutePoints] = useState(() => {
        // Rehydrate in DRIVE ORDER: origin → stops-before-destination →
        // destination → stops-after-destination. Stops carrying the
        // after_destination flag (2026-08-26 — "drop the sofa at
        // grandma's after the unload") slot in after the To. This also
        // fixes the old hydration bug where every saved stop rendered
        // BELOW the destination card while saving positionally.
        const parsed = parseStops(form.additional_stops);
        const before = [], after = [];
        parsed.forEach((s, i) => {
            stopIdRef.current = i + 1;
            const pt = { id: `stop-${i}`, address: s.address || '', pointType: 'stop', locPrefix: `stop_${i}` };
            (s.after_destination ? after : before).push(pt);
        });
        return [
            { id: 'origin', address: form.origin_address || '', pointType: 'origin', locPrefix: 'from' },
            ...before,
            { id: 'dest', address: form.destination_address || '', pointType: 'destination', locPrefix: 'to' },
            ...after,
        ];
    });

    const addRouteStop = () => {
        const idx = stopIdRef.current++;
        const stop = { id: `stop-${idx}-${Date.now()}`, address: '', pointType: 'stop', locPrefix: `stop_${idx}` };
        // New stops slot in BEFORE the destination (the common en-route
        // case). Drag / arrows move one below the To for after-unload
        // drops — or use "Set as destination" to re-point the To itself.
        setRoutePoints(prev => {
            const di = prev.findIndex(p => p.pointType === 'destination');
            if (di === -1) return [...prev, stop];
            const u = [...prev];
            u.splice(di, 0, stop);
            return u;
        });
    };

    // Designate a stop as THE destination (Jaylen 2026-08-26): with 3+
    // route points, which card is the main unload is a choice, not just
    // "whoever is last" — e.g. Origin → Stop → Destination → Stop
    // (sofa to grandma after the unload). The old destination becomes a
    // plain stop; drive order (card order) is untouched.
    const makeDestination = (id) => {
        setRoutePoints(prev => {
            const target = prev.find(p => p.id === id);
            if (!target || target.pointType !== 'stop') return prev;
            return prev.map(p =>
                p.id === id ? { ...p, pointType: 'destination' }
                : p.pointType === 'destination' ? { ...p, pointType: 'stop' }
                : p);
        });
    };
    const updateRouteAddress = (id, address) => setRoutePoints(prev => prev.map(p => p.id === id ? { ...p, address } : p));
    const removeRoutePoint = (id) => setRoutePoints(prev => prev.filter(p => p.id !== id));
    const moveRoutePoint = (idx, direction) => {
        const newIdx = idx + direction;
        if (newIdx < 0 || newIdx >= routePoints.length) return;
        setRoutePoints(prev => { const u = [...prev]; const [m] = u.splice(idx, 1); u.splice(newIdx, 0, m); return u; });
    };

    const [dragIdx, setDragIdx] = useState(null);
    const [dragOverIdx, setDragOverIdx] = useState(null);
    const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', idx); };
    const onDragOver = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverIdx !== idx) setDragOverIdx(idx); };
    const onDrop = (e, idx) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
        setRoutePoints(prev => { const u = [...prev]; const [m] = u.splice(dragIdx, 1); u.splice(idx, 0, m); return u; });
        setDragIdx(null); setDragOverIdx(null);
    };
    const onDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

    useEffect(() => {
        if (routePoints.length === 0 || readOnly) return;
        // Serialize by DESIGNATION, not position (2026-08-26): the origin
        // card is the origin, the designated destination card is the
        // destination wherever it sits in drive order, and every other
        // point is a stop. Stops after the destination carry
        // after_destination:true so the crew portal / navigation sequence
        // them after the To. (Previously destination_address was simply
        // whoever was last — the labels and the save could disagree.)
        const originPt = routePoints.find(p => p.pointType === 'origin') || routePoints[0];
        let destPt = routePoints.find(p => p.pointType === 'destination') || null;
        if (!destPt && routePoints.length > 1) destPt = routePoints[routePoints.length - 1];
        const destIdx = destPt ? routePoints.indexOf(destPt) : -1;
        const others = routePoints.filter(p => p !== originPt && p !== destPt);
        const stopsStr = others.length > 0 ? JSON.stringify(others.map(p => ({
            address: p.address,
            ...(destIdx >= 0 && routePoints.indexOf(p) > destIdx ? { after_destination: true } : {}),
        }))) : '';
        const firstAddr = originPt?.address || '';
        const lastAddr = destPt?.address || '';
        if (setBatch) {
            setBatch({ origin_address: firstAddr, destination_address: lastAddr, additional_stops: stopsStr });
        } else {
            set('origin_address', firstAddr);
            set('destination_address', lastAddr);
            set('additional_stops', stopsStr);
        }
    }, [routePoints, readOnly]);

    const OFFICE_ADDRESS = '1534 Haywood Rd, Hendersonville, NC 28739, USA';
    const [legs, setLegs] = useState([]);
    const [legsLoading, setLegsLoading] = useState(false);
    const prevRouteKey = useRef('');

    const getRouteAddresses = () => {
        // Office wraps EVERY job type now (Jaylen 2026-08-26): the pull-out
        // from the office and the deadhead back are real drives on OOS
        // trips too — the totals panel shows both the client route and the
        // full office round trip.
        const pts = [];
        routePoints.forEach(p => { if (p.address) pts.push(p.address); });
        if (pts.length === 0) return [];
        return [OFFICE_ADDRESS, ...pts, OFFICE_ADDRESS];
    };

    useEffect(() => {
        if (!showDistances) return;
        const addrs = getRouteAddresses();
        const routeKey = addrs.join('|||');
        if (routeKey === prevRouteKey.current) return;
        prevRouteKey.current = routeKey;
        if (addrs.length < 2) { setLegs([]); return; }
        const fetchLegs = async () => {
            if (!window.google?.maps) return;
            setLegsLoading(true);
            try {
                const service = new window.google.maps.DistanceMatrixService();
                const results = [];
                for (let i = 0; i < addrs.length - 1; i++) {
                    const resp = await new Promise((resolve, reject) => {
                        service.getDistanceMatrix({ origins: [addrs[i]], destinations: [addrs[i + 1]], travelMode: 'DRIVING', unitSystem: window.google.maps.UnitSystem.IMPERIAL },
                            (response, status) => { if (status === 'OK') resolve(response); else reject(status); });
                    });
                    const el = resp?.rows?.[0]?.elements?.[0];
                    results.push(el?.status === 'OK' ? { distance: el.distance.text, duration: el.duration.text, distanceM: el.distance.value, durationS: el.duration.value } : null);
                }
                setLegs(results);
            } catch(e) { setLegs([]); }
            finally { setLegsLoading(false); }
        };
        const timer = setTimeout(fetchLegs, 800);
        return () => clearTimeout(timer);
    }, [routePoints, form.job_type, showDistances]);

    const legOffsetBase = 1; // legs[0] is always Office → first point now
    // Trip totals with a labeled per-leg breakdown (Jaylen 2026-08-26).
    // The old bar showed one unlabeled number with "(1 leg · incl. office)"
    // — which read like a one-way distance. Now the HEADLINE is the client
    // route (origin → stops → destination — the drive that's about the
    // customer), every leg is named (Office → Origin, Origin → Stop 2,
    // ...), and the bottom row is the full ROUND TRIP from/back to the
    // office (1534 Haywood Rd) — for every job type.
    const tripTotals = useMemo(() => {
        const validLegs = legs.filter(l => l);
        if (validLegs.length === 0) return null;
        const fmtDur = (s) => Math.floor(s / 3600) > 0 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m` : `${Math.round(s / 60)} min`;
        const fmtSum = (arr) => {
            const m = arr.reduce((sum, l) => sum + (l.distanceM || 0), 0);
            const s = arr.reduce((sum, l) => sum + (l.durationS || 0), 0);
            return { miles: (m / 1609.34).toFixed(1), time: fmtDur(s) };
        };
        // Labels parallel the addrs array the legs were fetched from:
        // [Office, ...addressed route points..., Office]. The designated
        // destination keeps its name even mid-route (grandma's-sofa case).
        const addressed = routePoints.filter(p => p.address);
        const ptLabels = addressed.map(p =>
            p.pointType === 'origin' ? 'Origin'
            : p.pointType === 'destination' ? 'Destination'
            : `Stop ${routePoints.indexOf(p) + 1}`);
        const allLabels = ['Office', ...ptLabels, 'Office'];
        // The legs state refreshes async — if it lags a route edit, skip
        // the breakdown rather than mislabel legs (totals still render).
        const labelsInSync = allLabels.length === legs.length + 1;
        const legRows = labelsInSync
            ? legs.map((l, i) => l ? {
                from: allLabels[i], to: allLabels[i + 1],
                distance: l.distance, duration: l.duration,
                distanceM: l.distanceM, durationS: l.durationS,
                isOffice: i === 0 || i === legs.length - 1,
            } : null).filter(Boolean)
            : [];
        const clientLegs = legRows.filter(l => !l.isOffice);
        const clientSum = clientLegs.length > 0 ? fmtSum(clientLegs) : null;
        const allSum = fmtSum(validLegs);
        return {
            // Headline = the client's route; falls back to everything we
            // have when the breakdown can't be labeled yet.
            miles: (clientSum || allSum).miles,
            time: (clientSum || allSum).time,
            headlineLabel: clientSum ? 'client route' : 'route total',
            legs: validLegs.length,
            legRows,
            roundTrip: (labelsInSync && legRows.some(l => l.isOffice)) ? allSum : null,
        };
    }, [legs, routePoints]);

    if (readOnly) {
        return (
            <div className="space-y-2 text-xs">
                {routePoints.map((point, idx) => {
                    const num = idx + 1;
                    const typeLabel = point.pointType === 'origin' ? 'From' : point.pointType === 'destination' ? 'To' : `Stop ${num}`;
                    const dotColor = point.pointType === 'origin' ? 'bg-red-500' : point.pointType === 'destination' ? 'bg-blue-500' : 'bg-emerald-500';
                    const legAfterIdx = legOffsetBase + idx;
                    const legAfter = legs[legAfterIdx];
                    const hasNext = idx < routePoints.length - 1;
                    return (
                        <React.Fragment key={point.id}>
                            <div className="flex items-center gap-2">
                                <div className={`w-4 h-4 ${dotColor} rounded-full flex items-center justify-center flex-shrink-0`}><span className="text-white text-[8px] font-bold">{num}</span></div>
                                <span className="font-bold text-slate-500 whitespace-nowrap">{typeLabel}:</span>
                                {point.address ? (
                                    /* Read-only address — keeps the Street View thumbnail +
                                       Google Maps / Zillow links available in view mode. */
                                    <div className="flex-1 min-w-0">
                                        <AddressInput value={point.address} readOnly className="w-full px-0 py-0 border-0 bg-transparent text-xs text-slate-700 focus:outline-none cursor-default truncate" />
                                    </div>
                                ) : (
                                    <span className="text-slate-400">Not set</span>
                                )}
                            </div>
                            {hasNext && point.address && routePoints[idx + 1]?.address && legAfter && (
                                <div className="ml-6 flex items-center gap-1 text-[10px] text-slate-400">
                                    <span>↓</span>
                                    <span className="font-medium">{legAfter.distance} · {legAfter.duration}</span>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
                {tripTotals && (
                    <div className="bg-slate-100 rounded-lg px-2.5 py-2 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <IconTruck size={12} className="text-slate-400 flex-shrink-0"/>
                            <span className="font-bold text-slate-600">Trip total: {tripTotals.miles} mi · {tripTotals.time}</span>
                            <span className="text-slate-400">{tripTotals.headlineLabel}</span>
                        </div>
                        {/* Named leg breakdown — office out-and-back legs muted,
                            full office round trip on the bottom row (Jaylen
                            2026-08-26). */}
                        {tripTotals.legRows.length > 0 && (
                            <div className="border-t border-slate-200 pt-1 space-y-0.5">
                                {tripTotals.legRows.map((l, i) => (
                                    <div key={i} className={`flex items-center justify-between gap-2 text-[10px] ${l.isOffice ? 'text-slate-400' : 'text-slate-600'}`}>
                                        <span className="truncate">{l.from} → {l.to}</span>
                                        <span className="font-medium whitespace-nowrap">{l.distance} · {l.duration}</span>
                                    </div>
                                ))}
                                {tripTotals.roundTrip && (
                                    <div className="flex items-center justify-between gap-2 text-[10px] text-emerald-700 font-semibold border-t border-slate-200 pt-1 mt-0.5">
                                        <span>Round trip — office out &amp; back</span>
                                        <span className="whitespace-nowrap">{tripTotals.roundTrip.miles} mi · {tripTotals.roundTrip.time}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <div className="pt-2 border-t border-slate-100 grid grid-cols-2 gap-x-4 gap-y-1">
                    {form.from_sqft > 0 && <div><span className="text-slate-400">Origin Sqft:</span> {form.from_sqft}</div>}
                    {form.from_stairs && <div className="text-orange-600">Origin: Stairs ({form.from_levels || 1} levels)</div>}
                    {form.from_elevator && <div>Origin: Elevator</div>}
                    {form.from_apartment && <div>Origin: Apt {form.from_apartment_floor ? `(Floor ${form.from_apartment_floor})` : ''}</div>}
                    {form.to_sqft > 0 && <div><span className="text-slate-400">Dest Sqft:</span> {form.to_sqft}</div>}
                    {form.to_stairs && <div className="text-orange-600">Dest: Stairs ({form.to_levels || 1} levels)</div>}
                    {form.to_elevator && <div>Dest: Elevator</div>}
                    {form.to_apartment && <div>Dest: Apt {form.to_apartment_floor ? `(Floor ${form.to_apartment_floor})` : ''}</div>}
                    {form.heavy_items && <div className="col-span-2"><span className="text-slate-400">Heavy Items:</span> {form.heavy_items}</div>}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {showDistances && routePoints[0]?.address && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
                    <span className="text-xs">&#x1F3E2;</span>
                    <span className="text-[10px] font-semibold text-slate-400">Office</span>
                    {legs[0] && (<span className="ml-auto text-[10px] text-slate-400">{legs[0].distance} · {legs[0].duration}</span>)}
                </div>
            )}

            <div className="space-y-1">
            {routePoints.map((point, idx) => {
                const num = idx + 1;
                let cardBg, cardBorder, dotBg, labelColor, labelText, isRequired;
                if (point.pointType === 'origin') {
                    cardBg = 'bg-red-50/60'; cardBorder = 'border-red-200'; dotBg = 'bg-red-600'; labelColor = 'text-red-700'; labelText = 'Moving From (Origin)'; isRequired = true;
                } else if (point.pointType === 'destination') {
                    cardBg = 'bg-blue-50/60'; cardBorder = 'border-blue-200'; dotBg = 'bg-blue-600'; labelColor = 'text-blue-700'; labelText = 'Moving To (Destination)'; isRequired = false;
                } else {
                    const sc = [['bg-emerald-50/60','border-emerald-200','bg-emerald-600','text-emerald-700'],['bg-amber-50/60','border-amber-200','bg-amber-600','text-amber-700'],['bg-purple-50/60','border-purple-200','bg-purple-600','text-purple-700'],['bg-cyan-50/60','border-cyan-200','bg-cyan-600','text-cyan-700']][idx % 4];
                    cardBg = sc[0]; cardBorder = sc[1]; dotBg = sc[2]; labelColor = sc[3]; labelText = `Stop ${num}`; isRequired = false;
                }
                const legAfter = legs[legOffsetBase + idx];
                const hasNext = idx < routePoints.length - 1;
                const canRemove = point.pointType === 'stop';
                const isDragging = dragIdx === idx;
                const isOver = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;

                return (
                    <React.Fragment key={point.id}>
                        {/* Drop target is the whole card; the DRAG SOURCE is only
                            the grip handle below — so click-dragging inside the
                            address field selects text instead of grabbing the box
                            (Jaylen 2026-06-29). */}
                        <div onDragOver={(e) => onDragOver(e, idx)} onDrop={(e) => onDrop(e, idx)}
                            className={`${cardBg} border ${cardBorder} rounded-lg px-3 py-2.5 transition-all ${isDragging ? 'opacity-40' : ''} ${isOver ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}>
                            <div className="flex items-center gap-2">
                                {routePoints.length >= 3 && (
                                    <div className="flex flex-col items-center gap-px -ml-1 mr-0.5">
                                        <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (idx > 0) moveRoutePoint(idx, -1); }}
                                            className={`w-5 h-4 flex items-center justify-center cursor-pointer select-none text-[10px] leading-none ${idx === 0 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-700'}`} role="button">&#x25B2;</span>
                                        <div draggable onDragStart={(e) => onDragStart(e, idx)} onDragEnd={onDragEnd}
                                            className="text-slate-400 hover:text-slate-700 cursor-grab active:cursor-grabbing text-xs leading-none select-none px-1 py-0.5" title="Drag to reorder">&#x2807;</div>
                                        <span onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (idx < routePoints.length - 1) moveRoutePoint(idx, 1); }}
                                            className={`w-5 h-4 flex items-center justify-center cursor-pointer select-none text-[10px] leading-none ${idx === routePoints.length - 1 ? 'text-slate-200' : 'text-slate-400 hover:text-slate-700'}`} role="button">&#x25BC;</span>
                                    </div>
                                )}
                                <div className={`w-5 h-5 ${dotBg} rounded-full flex items-center justify-center flex-shrink-0`}><span className="text-white text-[9px] font-bold">{num}</span></div>
                                <span className={`text-[11px] font-bold ${labelColor} uppercase tracking-wide`}>{labelText}</span>
                                {isRequired && <span className="text-red-400 text-[10px]">*</span>}
                                <div className="flex-1"></div>
                                {/* With 3+ points the main unload is a CHOICE —
                                    any stop can be re-pointed as the Destination
                                    (Jaylen 2026-08-26: Origin → Stop → Destination
                                    → drop the sofa at grandma's). */}
                                {point.pointType === 'stop' && routePoints.length >= 3 && (
                                    <Button variant="secondary" size="sm" className="mr-1" onClick={() => makeDestination(point.id)}
                                        title="Make this the destination (the main unload) — the current Destination becomes a stop; drive order stays as shown">
                                        Set as destination
                                    </Button>
                                )}
                                {canRemove && <IconButton label="Remove stop" size="sm" tone="danger" onClick={() => removeRoutePoint(point.id)}>&times;</IconButton>}
                            </div>
                            <div className={`${routePoints.length >= 3 ? 'ml-7' : ''} mt-1.5`}>
                                <AddressInput className={`${inputCls} bg-white text-sm`} value={point.address} onChange={v => updateRouteAddress(point.id, v)} placeholder={`${labelText} address...`} />
                            </div>
                        </div>
                        {showDistances && hasNext && point.address && routePoints[idx + 1]?.address && legAfter && (
                            <div className="flex items-center justify-center">
                                <div className="flex items-center gap-1.5 px-2 py-0.5">
                                    <div className="w-px h-2 bg-slate-300"></div>
                                    <span className="text-[10px] font-medium text-slate-400">{legAfter.distance} · {legAfter.duration}</span>
                                    <div className="w-px h-2 bg-slate-300"></div>
                                </div>
                            </div>
                        )}
                    </React.Fragment>
                );
            })}
            </div>

            {showDistances && routePoints[routePoints.length - 1]?.address && (() => {
                const retIdx = legOffsetBase + routePoints.filter(p => p.address).length - 1;
                const retLeg = legs[retIdx];
                return (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
                        <span className="text-xs">&#x1F3E2;</span>
                        <span className="text-[10px] font-semibold text-slate-400">Return to Office</span>
                        {retLeg && (<span className="ml-auto text-[10px] text-slate-400">{retLeg.distance} · {retLeg.duration}</span>)}
                    </div>
                );
            })()}

            <Button variant="secondary" size="sm" full onClick={addRouteStop} leftIcon={<IconPlus size={14}/>}>
                Add Stop {routePoints.length + 1}
            </Button>

            {showDistances && tripTotals && (
                <div className="bg-slate-800 text-white rounded-xl p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <IconTruck size={15} className="text-slate-300 flex-shrink-0"/>
                            <span className="text-xs font-bold uppercase tracking-wide whitespace-nowrap">Trip Total</span>
                            <span className="text-[10px] text-slate-400 truncate">{tripTotals.headlineLabel}</span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-bold">{tripTotals.miles} mi</span>
                            <span className="text-slate-500">·</span>
                            <span className="text-sm font-semibold text-slate-300">{tripTotals.time}</span>
                        </div>
                    </div>
                    {/* Labeled leg-by-leg breakdown (Jaylen 2026-08-26 —
                        "trip total" used to read like a one-way number). */}
                    {tripTotals.legRows.length > 0 && (
                        <div className="border-t border-slate-700 pt-2 space-y-1">
                            {tripTotals.legRows.map((l, i) => (
                                <div key={i} className={`flex items-center justify-between gap-2 text-[11px] ${l.isOffice ? 'text-slate-400' : 'text-slate-200'}`}>
                                    <span className="truncate">{l.from} → {l.to}</span>
                                    <span className="font-medium whitespace-nowrap">{l.distance} · {l.duration}</span>
                                </div>
                            ))}
                            {tripTotals.roundTrip && (
                                <div className="flex items-center justify-between gap-2 text-[11px] text-emerald-300 font-semibold border-t border-slate-700 pt-1.5 mt-1.5">
                                    <span>Round trip — office out &amp; back</span>
                                    <span className="whitespace-nowrap">{tripTotals.roundTrip.miles} mi · {tripTotals.roundTrip.time}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {!compact && (
                <div className="border-t border-slate-200 pt-4 mt-4">
                    <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        Location Details
                        <span className="text-[10px] text-slate-300 font-normal">(sq ft, stairs, elevator, driveway)</span>
                    </h3>
                    <div className="space-y-4">
                        {routePoints.map((point, idx) => {
                            const num = idx + 1;
                            if (point.pointType === 'origin') return <LocationFields key={point.id} prefix="from" label={`Stop ${num} — Origin`} form={form} set={set} color="red" readOnly={readOnly} />;
                            if (point.pointType === 'destination') return <LocationFields key={point.id} prefix="to" label={`Stop ${num} — Destination`} form={form} set={set} color="blue" readOnly={readOnly} />;
                            return <StopLocationFields key={point.id} stopIndex={idx} num={num} form={form} set={set} readOnly={readOnly} />;
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
